import type { AisTrackForm } from "@/entity/logistics/ship";
import { wharfService } from "@/api/services/wharfService";
import type {
    FindLocationPort,
    FindShipAreaLoader,
    FindShipDetail,
    FindShipExtentLoader,
    FindShipLoader,
    FindShipRow,
    FindShipTrackLoader
} from "./types";

/** 使用 Vue 版本的关键字接口，不在迁移层新增后端契约。 */
export const loadFindShips: FindShipLoader = async keyword => {
    const { shipService } = await import("@/api/services/shipService");
    return shipService.searchVo(keyword);
};

/** 查船详情沿用 Vue 的 /ship/find-create/:mmsi 语义。 */
export const loadFindShipDetail = async (mmsi: string): Promise<FindShipDetail> => {
    const { shipService } = await import("@/api/services/shipService");
    return shipService.findCreate(mmsi);
};

/** AIS 轨迹接口由现有 service 统一负责时间戳转换。 */
export const loadFindShipTrack: FindShipTrackLoader = async (form: AisTrackForm) => {
    const { shipService } = await import("@/api/services/shipService");
    return shipService.aisTrack(form);
};

/** 查船地图范围接口沿用 Vue shipService.extent 的四角坐标契约。 */
export const loadFindShipExtent: FindShipExtentLoader = async query => {
    const { shipService } = await import("@/api/services/shipService");
    return (await shipService.extent(query)) as unknown as FindShipRow[];
};

/** 自定义区域查船沿用 Vue shipService.getShipCustom 的 geoPointList/time 契约。 */
export const loadFindShipArea: FindShipAreaLoader = async query => {
    const { shipService } = await import("@/api/services/shipService");
    return (await shipService.getShipCustom(query)) as unknown as FindShipRow[];
};

/** 港口与码头地图点选均复用 Vue wharfService.getInfo 详情接口。 */
export const loadFindPort = async (id: string | number): Promise<FindLocationPort> => {
    return (await wharfService.getInfo(id)) as unknown as FindLocationPort;
};

/** 保留独立的码头加载器，便于地图 selectWharf 回调注入测试或替换实现。 */
export const loadFindWharf = loadFindPort;

/** 保留一个低层 HTTP 适配器，便于页面消费者在需要时读取原始位置数据。 */
export async function loadFindShipLocation(mmsi: string): Promise<FindShipDetail> {
    const { shippingRequestHttp } = await import("@/api");
    const response = await shippingRequestHttp.service.get<FindShipDetail>("/ship/location", { params: { mmsi } });
    return response.data;
}

export function normalizeFindShipRow(row: FindShipRow): { mmsi: string; name: string; englishName: string } {
    const ship = row.ship || row;
    return {
        mmsi: String(ship.id ?? row.id ?? ""),
        name: String(ship.cnname ?? row.cnname ?? "未命名船舶"),
        englishName: String(ship.name ?? row.name ?? "")
    };
}

/** 将地图组件返回的多种区域点格式归一化为有效的经纬度数组。 */
export function parseFindLocationCoordinates(value?: unknown): [number, number][] {
    if (!Array.isArray(value)) return [];
    return value.flatMap(point => {
        if (typeof point === "string") {
            const [lng, lat] = point.split(",").map(Number);
            return Number.isFinite(lng) && Number.isFinite(lat) ? ([[lng, lat]] as [number, number][]) : [];
        }
        if (Array.isArray(point)) {
            const [lng, lat] = point.map(Number);
            return Number.isFinite(lng) && Number.isFinite(lat) ? ([[lng, lat]] as [number, number][]) : [];
        }
        if (point && typeof point === "object") {
            const coordinate = point as { lng?: unknown; lon?: unknown; lat?: unknown };
            const lng = Number(coordinate.lng ?? coordinate.lon);
            const lat = Number(coordinate.lat);
            return Number.isFinite(lng) && Number.isFinite(lat) ? ([[lng, lat]] as [number, number][]) : [];
        }
        return [];
    });
}

/** 兼容 Vue 自定义搜船接口，将地图坐标转换成 GeoPoint 列表。 */
export function toFindLocationGeoPointList(points: number[][]): Array<{ lng: number; lat: number }> {
    return parseFindLocationCoordinates(points).map(([lng, lat]) => ({
        lng: Number(lng.toFixed(6)),
        lat: Number(lat.toFixed(6))
    }));
}
